import { type FC, Fragment, useState } from 'react'

import { type SearchContextClient } from '@/contexts/SearchContext'
import { getTreeFromFilters } from '@/domain/filters'
import { getExpandedFilterIdList } from '@/domain/srp/filters/getExpandedFilterIdList'
import {
  type AppliedAttributeFilter,
  type AppliedRangeFilter,
  type AttributeFilter,
  type DateFilter as DateFilterType,
  type FilterGroup,
  type GetSearchResultsPageByUrlQuery,
  type RangeFilter,
} from '@/generated'
import { useGetSearchResultsData } from '@/hooks/srp/useGetSearchResultsData'
import { type SearchActionsResult, useSearchActions } from '@/hooks/srp/useSearchActions'
import { trackEvent } from '@/lib/ga'
import { GA_EVENT } from '@/lib/ga/constants/gaEvent'
import { type RangeFilterMinMax } from '@/types/search'
import { Accordion } from '@/ui/atoms/accordion'
import { isAnyVacationRentalsCategory } from '@/utils/category/categoryTree'

import { CategoryTreeFilter } from '../filter-types/CategoryTreeFilter'
import { LocationTreeFilter } from '../filter-types/LocationTreeFilter'
import { FiltersDivider } from '../styled'
import { FilterGroup as FilterGroupComponent } from './FilterGroup'

type CommonFilterProps = {
  isMobile?: boolean
  /** Label of the filter accordion */
  label: string
  /** Canonical name of the filter */
  name: string
  /** Parent filter information */
  parentFilter?: { isSelected: boolean; label: string }
  /** Function to refetch results on filter selection */
  refetchResults: SearchActionsResult['refetchResults']
  /** Available SRP data */
  data?: GetSearchResultsPageByUrlQuery['searchResultsPageByUrl']
}

export type AttributeFilterProps = CommonFilterProps & {
  /** Applied attribute filter list */
  appliedFilters: AppliedAttributeFilter[]
  /** Current attribute filter to be rendered */
  attributeFilter: AttributeFilter
}

export type RangeFilterProps = CommonFilterProps & {
  /** Applied range filter list */
  appliedFilters: AppliedRangeFilter[]
  /** Current range filter to be rendered */
  rangeFilter: RangeFilterMinMax
}

export type FilterComponentProps = {
  label: FilterGroup['label']
  name: FilterGroup['name']
  parentFilter?: { isSelected: boolean; label: string }
  onDateFilterChange: SearchContextClient['handleDateFiltersChange']
  resetDateFilter: SearchContextClient['resetDateFilter']
  onDateRangeFilterChange: SearchContextClient['handleDateRangeFiltersChange']
  rangeFilter?: RangeFilter
  dateFilter?: DateFilterType
  isSrpLoading?: boolean
}

export type FiltersAccordionProps = {
  isMobile?: boolean
}

export const FiltersAccordion: FC<FiltersAccordionProps> = ({ isMobile }) => {
  const { data, setLoadingStates } = useGetSearchResultsData()
  const { controls, searchQuery } = data || {}

  const filters = controls?.filtering
  const selectedCategoryId = searchQuery?.category?.id || 0

  const searchActionProps = useSearchActions(searchQuery?.filters)
  const preExpandedFilters = isMobile ? [] : getExpandedFilterIdList(selectedCategoryId)
  const [expandedFilters, setExpandedFilters] = useState(preExpandedFilters)

  /**
   * callback that fires when an accordion item is clicked
   * i.e. when a user opens/closes a filter
   * @param uuidArray contains a list of uuids for all expanded filters
   */
  const handleAccordionItemClick = (uuidArray: string[]) => {
    // only fire tracking event when the filter opens, not when it closes
    if (uuidArray.length > expandedFilters.length) {
      trackEvent({ action: GA_EVENT.FilterSelectBegin })
    }

    setExpandedFilters(uuidArray)
  }

  const hasFilterGroups = !!filters && filters.length > 0
  if (!hasFilterGroups) return null

  const { categoryTree, locationTree } = getTreeFromFilters(filters)

  return (
    <>
      <Accordion gap={0} preExpanded={preExpandedFilters} onChange={handleAccordionItemClick}>
        {!!categoryTree && (
          <CategoryTreeFilter
            key="category-tree-filter"
            data={data}
            categoryTree={categoryTree}
            refetchResults={searchActionProps.refetchResults}
            setLoadingStates={setLoadingStates}
          />
        )}
        {!!locationTree && !isAnyVacationRentalsCategory(selectedCategoryId) && (
          <Fragment key="location-tree-filter">
            <FiltersDivider />
            <LocationTreeFilter
              refetchResults={searchActionProps.refetchResults}
              data={data}
              locationTree={locationTree}
            />
          </Fragment>
        )}
        {filters.map((item, index) => {
          return (
            <FilterGroupComponent
              filterGroup={item}
              filterGroups={filters}
              key={index}
              {...searchActionProps}
            />
          )
        })}
      </Accordion>
    </>
  )
}
